// src/lib/smartAlerts.ts
import { supabase } from '../supabaseClient';

export type SmartAlertType =
  | 'last_call'
  | 'region'
  | 'restaurant'
  | 'hotel'
  | 'gift'
  | 'family'
  | 'evasion'
  | 'flight'
  | 'corporate';

export type SmartAlertFrequency = 'instant' | 'daily' | 'weekly';
export type SmartAlertStatus = 'active' | 'paused' | 'archived';

export interface SmartAlertPayload {
  email: string;
  name?: string;
  alert_type: SmartAlertType;
  region?: string;
  city?: string;
  category?: string;
  max_budget?: string | number;
  keywords?: string;
  occasion?: string;
  frequency?: SmartAlertFrequency;
  language?: 'fr' | 'en';
  consent?: boolean;
  page_url?: string;
  origin?: string;
  destination?: string;
  travel_period?: string;
  passengers?: string | number;
  direct_only?: boolean;
  source?: string;
  flexibility?: string;
}


export interface SmartAlert extends SmartAlertPayload {
  id: string;
  user_id?: string | null;
  status: SmartAlertStatus;
  email_enabled?: boolean;
  manage_token?: string | null;
  matched_count?: number;
  last_sent_at?: string | null;
  next_scan_at?: string | null;
  created_at?: string;
  updated_at?: string;
  metadata?: Record<string, unknown> | null;
}

export interface SmartAlertResult {
  ok: boolean;
  alert?: SmartAlert;
  alerts?: SmartAlert[];
  manage_token?: string;
  error?: string;
  details?: string;
}


export interface EvasionFlightAlertPayload {
  email: string;
  origin: string;
  destination: string;
  max_budget?: string | number;
  maxBudget?: string | number;
  budget?: string | number;
  travel_period?: string;
  travelPeriod?: string;
  passengers?: string | number;
  direct_only?: boolean;
  directOnly?: boolean;
}

export async function sendEvasionFlightAlert(payload: EvasionFlightAlertPayload): Promise<SmartAlertResult> {
  // Compatibilité : l'ancien nom crée maintenant une vraie alerte intelligente de vol,
  // puis la Edge Function smart-alerts envoie le courriel Kiwi affilié.
  return createSmartAlert({
    email: payload.email,
    alert_type: 'flight',
    origin: payload.origin,
    destination: payload.destination,
    max_budget: payload.max_budget ?? payload.maxBudget ?? payload.budget ?? '',
    travel_period: payload.travel_period ?? payload.travelPeriod ?? '',
    passengers: payload.passengers || '1',
    direct_only: Boolean(payload.direct_only ?? payload.directOnly),
    frequency: 'instant',
    language: 'fr',
    consent: true,
    page_url: typeof window !== 'undefined' ? window.location.href : undefined,
    source: 'legacy_send_evasion_flight_alert',
  });
}

const MANAGE_TOKEN_KEY = 'forfeo_smart_alert_manage_token';

export function getStoredSmartAlertToken() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(MANAGE_TOKEN_KEY) || '';
}

export function storeSmartAlertToken(token?: string | null) {
  if (typeof window === 'undefined' || !token) return;
  window.localStorage.setItem(MANAGE_TOKEN_KEY, token);
}

export async function invokeSmartAlerts(action: string, payload: Record<string, unknown> = {}): Promise<SmartAlertResult> {
  const { data: { session } } = await supabase.auth.getSession();
  const manageToken = getStoredSmartAlertToken();

  const { data, error } = await supabase.functions.invoke('smart-alerts', {
    body: {
      action,
      manage_token: manageToken || payload.manage_token || undefined,
      ...payload,
    },
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });

  if (error) {
    return { ok: false, error: error.message || 'function_error' };
  }

  if (data?.manage_token) storeSmartAlertToken(data.manage_token);
  return data as SmartAlertResult;
}

export async function createSmartAlert(payload: SmartAlertPayload) {
  return invokeSmartAlerts('create', payload as unknown as Record<string, unknown>);
}

export async function listSmartAlerts(email?: string) {
  return invokeSmartAlerts('list', email ? { email } : {});
}

export async function updateSmartAlert(id: string, patch: Partial<SmartAlertPayload & { status: SmartAlertStatus }>) {
  return invokeSmartAlerts('update', { id, patch });
}

export async function toggleSmartAlert(id: string, status: SmartAlertStatus) {
  return invokeSmartAlerts('toggle', { id, status });
}

export async function deleteSmartAlert(id: string) {
  return invokeSmartAlerts('delete', { id });
}
